// src/services/incidentReportService.js
import { db } from "../firebase";
import { summarizeIncidentEvents } from "./incidentEventService";
import { formatDuration } from "../utils/duration";
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

// Maps a problem categoryId to a human-readable resolution "lane".
const LANE_BY_CATEGORY = {
  "stripe-webhooks-events": "Webhooks & Event Delivery",
  "subscriptions-billing-logic": "Subscriptions & Billing",
  "checkout-payments-access": "Checkout & Payments",
  "connect-payouts-reliability": "Connect & Payouts",
};

export function deriveLane(categoryId) {
  return LANE_BY_CATEGORY[categoryId] || "General Payments";
}

// Deterministic 6-digit code derived from the problemId so the same problem
// always maps to the same report ID (idempotent, no collisions per problem).
function hashTo6Digits(input) {
  let h = 0;
  const s = String(input || "");
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return String(h % 1000000).padStart(6, "0");
}

/**
 * Builds the canonical report ID: INC-YYYY-XXXXXX
 * Exported so the form can display the exact ID that will be written.
 */
export function generateReportId(problemId, date = new Date()) {
  const year = date.getFullYear();
  return `INC-${year}-${hashTo6Digits(problemId)}`;
}

const WARRANTY_WINDOW_MS = 48 * 60 * 60 * 1000; // 48 hours

/**
 * Operational numbers the customer-facing report should carry.
 *
 * These are derived from the event log rather than from the problem document,
 * so they cannot drift from the timeline the report is supposed to summarise.
 */
export function buildResolutionMetrics(events = [], resolvedAtMs = Date.now()) {
  const summary = summarizeIncidentEvents(events);
  const timeToFirstResponseMs =
    summary.reportedAt && summary.firstResponseAt
      ? summary.firstResponseAt - summary.reportedAt
      : null;
  const timeToResolveMs = summary.reportedAt
    ? resolvedAtMs - summary.reportedAt
    : null;

  return {
    reportedAt: summary.reportedAt,
    assignedAt: summary.assignedAt,
    firstResponseAt: summary.firstResponseAt,
    workStartedAt: summary.workStartedAt,
    sessionCount: summary.sessionCount,
    sessionMs: summary.sessionMs,
    timeToFirstResponseMs,
    timeToResolveMs,
    timeToFirstResponseLabel: formatDuration(timeToFirstResponseMs),
    timeToResolveLabel: formatDuration(timeToResolveMs),
    sessionLabel: formatDuration(summary.sessionMs),
  };
}

/**
 * Writes the Incident Resolution Report and stamps the problem document.
 *
 * The resolutionIntelligence/{incidentId} document is written server-side by
 * the sendIncidentResolutionReport Cloud Function (triggered on the create of
 * the incidentReports doc), because that collection is writable only via the
 * Admin SDK per the security rules.
 *
 * @param {Object} args
 * @param {Object} args.problem        Full problem document (must include id, helperId, clientId).
 * @param {string} args.specialistId   The authenticated specialist (must equal problem.helperId).
 * @param {Object} args.form           Form values from IncidentReportForm.
 * @returns {Promise<{success: boolean, reportId?: string, error?: string}>}
 */
export async function submitIncidentReport({
  problem,
  specialistId,
  form,
  events = [],
}) {
  try {
    if (!problem?.id) {
      return { success: false, error: "Missing problem reference." };
    }
    if (problem.helperId !== specialistId) {
      return {
        success: false,
        error: "Only the assigned specialist can file this report.",
      };
    }

    const reportId = generateReportId(problem.id);
    const lane = deriveLane(problem.categoryId);
    const warrantyExpiresAt = Timestamp.fromMillis(
      Date.now() + WARRANTY_WINDOW_MS
    );

    const metrics = buildResolutionMetrics(events);

    const reportData = {
      reportId,
      problemId: problem.id,
      clientId: problem.clientId,
      specialistId,
      createdAt: serverTimestamp(),
      rootCause: (form.rootCause || "").trim(),
      actionsTaken: (form.actionsTaken || "").trim(),
      verificationSteps: Array.isArray(form.verificationSteps)
        ? form.verificationSteps
        : [],
      resolutionStatus: form.resolutionStatus,
      preventionNotes: (form.preventionNotes || "").trim(),
      sessionRecordingConsent: Boolean(form.sessionRecordingConsent),
      warrantyExpiresAt,
      metrics,
      // Payment context copied from the problem for the report record.
      paymentIntentId: problem.paymentIntentId || null,
      paymentTier: problem.paymentTier || null,
      amountPaid: typeof problem.amountPaid === "number" ? problem.amountPaid : null,
      lane,
    };

    // 1. Create the immutable report document (id == reportId).
    await setDoc(doc(db, "incidentReports", reportId), reportData);

    // 2. Stamp the problem document so the workspace can surface the report.
    await updateDoc(doc(db, "problems", problem.id), {
      incidentReportId: reportId,
      incidentReportStatus: form.resolutionStatus,
      resolvedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { success: true, reportId };
  } catch (error) {
    console.error("Error submitting incident report:", error);
    return { success: false, error: error.message };
  }
}
